import React, { useMemo, useState } from "react";
import { Modal, Dropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faDownload,
  faPrint,
  faCheck,
  faExclamation,
  faBan,
  faTimes,
  faClock,
  faList,
  faGavel,
  faMinusCircle,
  faQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { Gear } from "react-bootstrap-icons";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { saveAs } from "file-saver";

import FalconCloseButton from "components/common/FalconCloseButton";
import SubtleBadge from "components/common/SubtleBadge";
import CardDropdown from "components/common/CardDropdown";
import {
  ViewPreauthorization,
  GetPreauthorizationEligibility,
  GetPreauthorizationRequest,
  GetPreauthorizationResponse,
  DownloadResponseJSON,
  DownloadRequestJSON,
  GetPreauthorizationCancelResponse,
  ViewPreauthorizationHistoryLog,
} from "../config/preauthorizationApi";
import { getPathFromUrl } from "helpers/utils";
import ViewPreauthorizationModal from "./ViewPreauthorizationModal";
import {
  handleResetAll,
  handleResetHistoryLogs,
  resetPreauthCancel,
} from "../config/preauthorizationSlice";
import PrintComponent from "components/common/custom/print/PrintComponents";
import PreauthorizationPrintOCAF from "./PreauthorizationPrintOCAF";
import PreauthorizationPrintUCAF from "./PreauthorizationPrintUCAF";
import PreauthorizationPrintDCAF from "./PreauthorizationPrintDCAF";

const HistoryLogModal = ({ modal, setModal, row: modalRow }) => {
  const dispatch = useDispatch();
  const {
    preauthorizationHistoryLog,
  } = useSelector((state) => state.preauthorization);

  const [showViewModal, setShowViewModal] = useState(false);
  const [showUCAF, setShowUCAF] = useState(false);
  const [showDCAF, setShowDCAF] = useState(false);
  const [showOCAF, setShowOCAF] = useState(false);
  const [rowData, setRowData] = useState(null);
  const [printData, setPrintData] = useState(null);

  const handleView = async (preauthid, row) => {

    setShowViewModal(true);
    setRowData(row?.original);
  };

  const handlePrintUCAF = async (id, type, row) => {
    setRowData(row);
    const response = await dispatch(
      ViewPreauthorizationHistoryLog({
        preauthid: id,
        claim_type: row?.claim_type,
        preauth_status: row?.preauthsts,
        created_by: row?.created_by,
        created_at: row?.created_at
      })
    ).unwrap();

    if (response?.success) {
      setPrintData(response.data);

      if (type === "UCAF") setShowUCAF(true);
      if (type === "DCAF") setShowDCAF(true);
      if (type === "OCAF") setShowOCAF(true);
    }
  };

  const handleDownload = async (id, type, row) => {
    try {
      const fileUrl =
        type === "request" ? row?.preauthreq_path : row?.preauthresp_path;
      if (!fileUrl) throw new Error("File not found");
      const fileRes = await fetch(fileUrl);
      const blob = await fileRes.blob();
      const filename = fileUrl.split("/").pop();
      saveAs(blob, filename);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: ({ row }) => (
          <div
            className="rounded-circle bg-200 d-flex flex-center fs-11"
            style={{ width: 20, height: 20 }}
          >
            {row.index + 1}
          </div>
        ),
      },
      {
        accessorKey: "preauthsts",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue()?.toLowerCase();
          const statusConfig = {
            error: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
            Error: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
            invalid: { bg: "#fde6d8", color: "#ac5a2b", icon: "exclamation" },
            "no response": {
              bg: "#08101b",
              color: "#91979e",
              icon: "circle-exclamation", // clearer alert icon
            }, // NEW

            approved: { bg: "#d9f8eb", color: "#00894f", icon: "check" },
            valid: { bg: "#e6e8ec", color: "#5d6776", icon: "check" },
            rejected: { bg: "#fbdbe1", color: "#b82c46", icon: "times" },
            cancelled: { bg: "#ffe0fe", color: "#da23aa", icon: "times" },
            partial: {
              bg: "#C2EEF6",
              color: "#0da9bd",
              icon: "star-half-stroke",
            },
            "partially approved": {
              bg: "#C2EEF6",
              color: "#0da9bd",
              icon: "star-half-stroke",
            },
            pended: { bg: "#d9e7fa", color: "#215cac", icon: "clock" },
            "not-required": {
              bg: "#d8f3ff",
              color: "#197aa4",
              icon: "clock",
            },
            queued: { bg: "#fefad2", color: "#a7970d", icon: "list" },
            adjudication: { bg: "#e8dff7", color: "#6f42c1", icon: "gavel" },

            prepared: { bg: "#e6e8ec", color: "#5d6776", icon: "hammer" }, // NEW
          };

          const config = statusConfig[status] || {
            bg: "#f8f9fa",
            color: "#000",
            icon: "question",
          };

          return (
            <SubtleBadge
              pill
              className="d-flex flex-center fs-12 p-2 gap-1 text-capitalize"
              style={{
                backgroundColor: config.bg,
                color: config.color,
                border: `1px solid ${config.color}20`,
                width: "120px",
              }}
            >
              <FontAwesomeIcon icon={config.icon} className="me-2" />
              {getValue()}
            </SubtleBadge>
          );
        },
      },
      {
        accessorKey: "preauthrefno",
        header: "Preauth Ref No.",
        cell: () => modalRow?.preauthref || "-",
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        cell: ({ getValue }) =>
          moment(getValue()).format("DD-MM-YYYY, hh:mm:ss A"),
      },
      {
        accessorKey: "preauthid",
        header: "Preauthorization ID",
        cell: () => modalRow?.preauthfull_id || "-",
      },
      {
        accessorKey: "bundle_reqid",
        header: "Bundle ID",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "performed",
        header: "Performed By",
        cell: () =>
          `${modalRow?.employees_firstname || ""} ${modalRow?.employees_familyname || ""
          }`,
      },
      {
        accessorKey: "actions",
        header: () => <Gear />,
        enableSorting: false,
        cell: ({ row }) => {
          const id = row.original?.preauth_fullid;
          const showPrint = [
            "approved",
            "prepared",
            "partial",
            "cancelled",
            "rejected",
            "not required",
          ].includes(row?.original?.preauthsts?.toLowerCase());

          return (
            <CardDropdown icon="ellipsis-v">
              <Dropdown.Item onClick={() => handleView(id, row)}>
                <FontAwesomeIcon icon={faEye} className="me-2" />
                View Preauth
              </Dropdown.Item>

              {showPrint && (
                <>
                  <Dropdown.Item
                    onClick={() => handlePrintUCAF(id, "UCAF", row.original)}
                  >
                    <FontAwesomeIcon icon={faPrint} className="me-2" />
                    Print UCAF
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handlePrintUCAF(id, "DCAF", row.original)}
                  >
                    <FontAwesomeIcon icon={faPrint} className="me-2" />
                    Print DCAF
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handlePrintUCAF(id, "OCAF", row.original)}
                  >
                    <FontAwesomeIcon icon={faPrint} className="me-2" />
                    Print OCAF
                  </Dropdown.Item>
                </>
              )}

              <Dropdown.Item
                onClick={() => handleDownload(id, "request", row.original)}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download Request JSON
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleDownload(id, "response", row.original)}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download Response JSON
              </Dropdown.Item>
            </CardDropdown>
          );
        },
      },
    ],
    [modalRow, dispatch]
  );

  const table = useReactTable({
    data: preauthorizationHistoryLog || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <Modal
        show={modal}
        centered
        onHide={() => {
          setModal(false);
          dispatch(handleResetHistoryLogs());
        }}
        dialogClassName="history-log-modal"
        size="lg"
      >
        <Modal.Header className="d-flex justify-content-between align-items-center ps-4 pe-4 pt-3">
          <Modal.Title as="h5">History Log</Modal.Title>
          <FalconCloseButton
            size="sm"
            onClick={() => {
              setModal(false);
              dispatch(handleResetHistoryLogs());
            }}
            className="position-relative"
          />
        </Modal.Header>
        <Modal.Body
          className="p-4 modal-scroll-body border"
          style={{ minHeight: "300px" }}
        >
          <div className="table-responsive">
            <table className="table  table-sm fs-10 mb-0">
              <thead className="bg-200 text-dark text-nowrap align-middle">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} className="fw-semibold ">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="align-middle">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>

      <ViewPreauthorizationModal
        show={showViewModal}
        setShow={setShowViewModal}
        row={rowData}
      />
      {showUCAF && (
        <PrintComponent
          onPrintOpen={() => setShowUCAF(false)}
          child={
            <PreauthorizationPrintUCAF
              printData={printData}
              row={modalRow}
            />
          }
        />
      )}
      {showDCAF && (
        <PrintComponent
          onPrintOpen={() => setShowDCAF(false)}
          child={
            <PreauthorizationPrintDCAF
              printData={printData}
              show={modal}
            />
          }
        />
      )}
      {showOCAF && (
        <PrintComponent
          onPrintOpen={() => setShowOCAF(false)}
          child={
            <PreauthorizationPrintOCAF
              printData={printData}
              row={modalRow}
            />
          }
        />
      )}
    </>
  );
};

export default HistoryLogModal;
